// "Add passenger" / "Edit passenger" modal of the Passengers section (identity, passport, documents).
import type { FormInstance } from 'antd';
import { Button, Col, DatePicker, Divider, Form, Modal, Row, Select, Space, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

import type { AdminBookingPassenger } from '../../api/types';
import { CountryAutocomplete } from '../../components/CountryAutocomplete';
import { DocThumbs, UpperInput, notFuture, notPast } from '../../components/passengerBits';
import { LIMITS } from '../../data/limits';

const { Text } = Typography;

export function PassengerModal({
  form,
  editing,
  modalOpen,
  setModalOpen,
  submit,
  submitGuard,
  addMut,
  updateMut,
  uploading,
  doUpload,
  passportUrl,
  extraUrls,
  stagedDeletes,
  stageDelete,
}: {
  form: FormInstance;
  editing: AdminBookingPassenger | null;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  submit: () => void;
  submitGuard: { busy: boolean };
  addMut: { isPending: boolean };
  updateMut: { isPending: boolean };
  uploading: boolean;
  doUpload: (file: File, kind: 'passport' | 'extra') => Promise<boolean>;
  passportUrl: string | null;
  extraUrls: string[];
  stagedDeletes: string[];
  stageDelete: (url: string) => void;
}) {
  return (
    <Modal
      title={editing ? 'Edit passenger' : 'Add passenger'}
      open={modalOpen}
      onCancel={() => setModalOpen(false)}
      onOk={submit}
      confirmLoading={submitGuard.busy || addMut.isPending || updateMut.isPending}
      okText={editing ? 'Save' : 'Add'}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="fullName" label="Full name" rules={[{ required: true, message: 'Required' }]}>
          <UpperInput maxLength={LIMITS.name} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="dateOfBirth" label="Date of birth" rules={[{ required: true, message: 'Required' }]}>
              {/* SHAR-783 — a passenger cannot be born today or later. */}
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={notPast} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="gender" label="Gender" rules={[{ required: true, message: 'Required' }]}>
              <Select
                options={[
                  { value: 'Male', label: 'Male' },
                  { value: 'Female', label: 'Female' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="nationality" label="Nationality" rules={[{ required: true, message: 'Required' }]}>
          {/* SHAR-800 — filters as you type, matching the apps and the charter form. */}
          <CountryAutocomplete placeholder="Start typing a country" />
        </Form.Item>
        {/* SHAR-448 — no "reach out for passport details" checkbox in the admin
            form; all passport fields are always shown and editable. */}
        <>
                <Form.Item name="passportNumber" label="Passport number">
                  <UpperInput maxLength={LIMITS.passportNumber} />
                </Form.Item>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="issuingCountry" label="Issuing country">
                      <CountryAutocomplete placeholder="Start typing a country" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="passportExpiry" label="Passport expiry">
                      {/* SHAR-785 — an expired passport is not valid for travel. */}
                      <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={notFuture} />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider style={{ margin: '8px 0' }} />
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Upload showUploadList={false} beforeUpload={(f) => doUpload(f as File, 'passport')}>
                    <Button icon={<UploadOutlined />} loading={uploading}>
                      Upload passport photo
                    </Button>
                  </Upload>
                  <Upload showUploadList={false} beforeUpload={(f) => doUpload(f as File, 'extra')}>
                    <Button icon={<UploadOutlined />} loading={uploading}>
                      Add other document
                    </Button>
                  </Upload>
                  {/* SHAR-609 — what is already attached, as thumbnails that
                      open full size, each with a delete that only STAGES the
                      removal. Nothing is deleted until Save; Cancel leaves
                      every document untouched. */}
                  {passportUrl || extraUrls.length ? (
                    <>
                      <DocThumbs
                        docs={[
                          ...(passportUrl ? [{ url: passportUrl, label: 'Passport' }] : []),
                          ...extraUrls.map((u, i) => ({ url: u, label: `Doc ${i + 1}` })),
                        ]}
                        staged={stagedDeletes}
                        onStage={stageDelete}
                      />
                      {stagedDeletes.length ? (
                        <Text type="warning">
                          {stagedDeletes.length} document(s) will be deleted when you save. Cancel
                          to keep them.
                        </Text>
                      ) : null}
                    </>
                  ) : (
                    <Text type="secondary">No documents attached</Text>
                  )}
                </Space>
              </>
      </Form>
    </Modal>
  );
}
